/**
 * consultingApi — Supabase RPC wrappers for BAM Consulting flows.
 *
 * Two surfaces:
 *
 *   1. Admin org tooling (manual onboarding before Stripe goes live):
 *      - createOrg / addOrgMember / removeOrgMember
 *      - setOrgPartner (manual override)
 *      - listOrgs / listOrgMembers
 *      - refreshPartnerStatus (idempotent maintenance)
 *
 *   2. Self-serve checkout (once Stripe products are created):
 *      - createConsultingCheckout — calls the create-consulting-checkout
 *        edge function with the chosen tier + interval
 *
 * All admin RPCs reject non-admin callers with a 42501 PostgreSQL error;
 * the wrapper functions surface the message via thrown Error.
 */
import { supabase } from "../../supabaseClient";
import { fillProfiles } from "../utils/profileDisplay";
import { CONSULTING_TIERS, PARTNER_ORG_THRESHOLD_MONTHS } from "../constants/consulting";
import { authHeaders } from "../../utils/authHeaders";
import { assistHeaders } from "../utils/assistHeaders";
import { assertNotStaging } from "../../config/environment";

// ── Admin: org CRUD ─────────────────────────────────────────────────────

export async function adminCreateOrg({
  name,
  ownerUserId,
  tier = null,
  slug = null,
  logoUrl = null,
  website = null,
  city = null,
  country = null,
  startedAt = null,
}) {
  if (!name || !ownerUserId) throw new Error("name and ownerUserId required");
  const { data, error } = await supabase.rpc("admin_create_org", {
    p_name: name,
    p_owner_user_id: ownerUserId,
    p_tier: tier,
    p_slug: slug,
    p_logo_url: logoUrl,
    p_website: website,
    p_city: city,
    p_country: country,
    p_started_at: startedAt,
  });
  if (error) throw error;
  return data; // org_id (uuid)
}

export async function adminAddOrgMember(orgId, userId, role = "coach") {
  const { error } = await supabase.rpc("admin_add_org_member", {
    p_org_id: orgId,
    p_user_id: userId,
    p_role: role,
  });
  if (error) throw error;
}

export async function adminRemoveOrgMember(orgId, userId) {
  const { error } = await supabase.rpc("admin_remove_org_member", {
    p_org_id: orgId,
    p_user_id: userId,
  });
  if (error) throw error;
}

export async function adminSetOrgPartner(orgId, isPartner) {
  const { error } = await supabase.rpc("admin_set_org_partner", {
    p_org_id: orgId,
    p_is_partner: !!isPartner,
  });
  if (error) throw error;
}

export async function adminListOrgs() {
  const { data, error } = await supabase.rpc("admin_list_orgs");
  if (error) throw error;
  return data || [];
}

export async function adminListOrgMembers(orgId) {
  const { data, error } = await supabase.rpc("admin_list_org_members", {
    p_org_id: orgId,
  });
  if (error) throw error;
  return data || [];
}

export async function adminRefreshPartnerStatus() {
  const { data, error } = await supabase.rpc("refresh_org_partner_status");
  if (error) throw error;
  return data ?? 0; // count of newly promoted orgs
}

// ── Self-serve checkout (Stripe-backed once price IDs are set) ──────────
//
// Calls the create-consulting-checkout edge function. Returns a Stripe
// Checkout URL the caller can redirect to. Errors with a friendly message
// when Stripe price IDs are still placeholders.

export async function createConsultingCheckout({ tier, interval = "monthly", orgId = null, orgName = null }) {
  const def = CONSULTING_TIERS[tier];
  if (!def) throw new Error(`Unknown tier: ${tier}`);
  if (def.contact_only) {
    // Custom tier — no Stripe checkout. Caller should open mailto / contact form.
    return { contactOnly: true };
  }
  const priceDef = def.prices?.[interval];
  if (!priceDef?.stripe_price_id || priceDef.stripe_price_id.startsWith("price_TODO_")) {
    throw new Error("This plan is not yet available for self-serve. Contact us to get set up.");
  }

  assertNotStaging("Checkout");
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not authenticated");

  const baseUrl = process.env.REACT_APP_SUPABASE_URL || "https://rlfekxctjyvozujzbzwm.supabase.co";
  const res = await fetch(`${baseUrl}/functions/v1/create-consulting-checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      tier,
      interval,
      orgId,
      orgName,
      successUrl: `${window.location.origin}/?checkout=success#/org`,
      cancelUrl:  `${window.location.origin}/?checkout=cancelled#/consulting`,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.url) {
    throw new Error(json.error || `checkout ${res.status}`);
  }
  return { url: json.url };
}

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Compute whether an org has earned the BAM Partner badge given its
 * current state. Server has the authoritative `is_partner` column; this
 * helper exists for client-side tenure math (e.g. showing a "Partner in
 * 4 months" countdown on the org dashboard).
 *
 * Mirrors the SQL logic in refresh_org_partner_status: continuous active
 * billing for >= 6 months. Returns { isPartner, monthsActive, monthsToGo }.
 */
export function deriveOrgPartnerProgress({ org, consultingSubscription }) {
  if (!org) return { isPartner: false, monthsActive: 0, monthsToGo: PARTNER_ORG_THRESHOLD_MONTHS };
  if (org.is_partner) {
    return { isPartner: true, monthsActive: monthsBetween(org.partner_since, new Date()) || PARTNER_ORG_THRESHOLD_MONTHS, monthsToGo: 0 };
  }
  if (!consultingSubscription || !consultingSubscription.started_at) {
    return { isPartner: false, monthsActive: 0, monthsToGo: PARTNER_ORG_THRESHOLD_MONTHS };
  }
  if (!["active", "past_due", "trialing"].includes(consultingSubscription.status)) {
    return { isPartner: false, monthsActive: 0, monthsToGo: PARTNER_ORG_THRESHOLD_MONTHS };
  }
  const months = monthsBetween(consultingSubscription.started_at, new Date());
  return {
    isPartner: months >= PARTNER_ORG_THRESHOLD_MONTHS,
    monthsActive: months,
    monthsToGo: Math.max(0, PARTNER_ORG_THRESHOLD_MONTHS - months),
  };
}

function monthsBetween(fromIso, toDate) {
  if (!fromIso) return 0;
  const from = new Date(fromIso);
  if (isNaN(from)) return 0;
  const to = toDate || new Date();
  const ms = to.getTime() - from.getTime();
  if (ms <= 0) return 0;
  // Approximate — not calendar-precise but good enough for "X months in".
  return Math.floor(ms / (30.44 * 86400000));
}

// ── Custom org pricing (admin) ──────────────────────────────────────────

export async function adminInviteUser({ email, firstName, lastName }) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not authenticated");
  const res = await fetch("/api/admin-actions?action=invite-user", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ email, first_name: firstName, last_name: lastName, origin: window.location.origin }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `invite ${res.status}`);
  return json; // { user_id, email, was_existing }
}

export async function uploadOrgLogo(file) {
  // Use the existing `banners` storage bucket — it's public-read with
  // INSERT scoped to the uploader's own folder. Org logos go under
  // <admin-uid>/orgs/<filename>. Public URL works for direct <img> use.
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const ext = (file.name.split(".").pop() || "png").toLowerCase();
  const path = `${user.id}/orgs/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from("banners")
    .upload(path, file, { upsert: false, contentType: file.type || undefined });
  if (error) throw error;
  const { data } = supabase.storage.from("banners").getPublicUrl(path);
  return data?.publicUrl || null;
}

// ── Admin org tools, through the server (api/admin-actions?action=org-admin)
// The browser can't write organizations or read another org's members as an
// admin (RLS lets only the org's own owner/admin), so the price "saved" and
// matched 0 rows, and cert progress came back empty. Service role instead.

async function postAction(action, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`/api/admin-actions?action=${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `${action} ${res.status}`);
  return json;
}

export const adminOrg = (op, payload = {}) =>
  postAction("org-admin", { op, origin: window.location.origin, ...payload });

export async function adminSetOrgCustomPrice(orgId, priceCents) {
  return adminOrg("set_price", { org_id: orgId, cents: priceCents ?? null });
}

/** Members with names, emails, roles and cert status, plus the org row. */
export async function adminOrgMembers(orgId) {
  return adminOrg("members", { org_id: orgId });
}

/** Push (and for calls, email) to the rest of the org after a write.
 *  Best effort: a failed notification never fails the action itself. */
export function notifyOrg(kind, id) {
  if (!id) return;
  postAction("org-notify", { kind, id }).catch((e) => console.warn("org-notify:", e.message));
}

// ── Org Portal: Assignments ─────────────────────────────────────────────

export async function listOrgAssignments(orgId) {
  const { data, error } = await supabase
    .from("org_assignments")
    .select("*")
    .eq("org_id", orgId)
    .order("due_at", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return data || [];
}

export async function createOrgAssignment({ orgId, title, prompt, submissionType = "text", dueAt = null, quizId = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("org_assignments")
    .insert({
      org_id: orgId,
      created_by: user.id,
      title,
      prompt,
      submission_type: submissionType,
      due_at: dueAt,
      quiz_id: quizId,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteOrgAssignment(assignmentId) {
  const { error } = await supabase.from("org_assignments").delete().eq("id", assignmentId);
  if (error) throw error;
}

export async function listMyAssignmentSubmissions(assignmentIds) {
  if (!assignmentIds?.length) return [];
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from("org_assignment_submissions")
    .select("*")
    .in("assignment_id", assignmentIds)
    .eq("user_id", user.id);
  if (error) throw error;
  return data || [];
}

export async function listAssignmentSubmissions(assignmentId) {
  const { data, error } = await supabase
    .from("org_assignment_submissions")
    .select("*, profile:profiles!user_id(id, first_name, last_name, avatar_url)")
    .eq("assignment_id", assignmentId)
    .order("submitted_at", { ascending: false });
  if (error) throw error;
  return fillProfiles(data || []);
}

export async function submitAssignment({ assignmentId, body, videoUrl, fileUrl }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("org_assignment_submissions")
    .upsert({
      assignment_id: assignmentId,
      user_id: user.id,
      body: body || null,
      video_url: videoUrl || null,
      file_url: fileUrl || null,
      status: "submitted",
      submitted_at: new Date().toISOString(),
    }, { onConflict: "assignment_id,user_id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function reviewAssignmentSubmission(submissionId, { reviewerNote, status = "reviewed" }) {
  const { error } = await supabase
    .from("org_assignment_submissions")
    .update({ reviewer_note: reviewerNote, status, reviewed_at: new Date().toISOString() })
    .eq("id", submissionId);
  if (error) throw error;
}

// ── Org Portal: Messages ────────────────────────────────────────────────

export async function listOrgMessages(orgId, { limit = 200 } = {}) {
  const { data, error } = await supabase
    .from("org_messages")
    .select("*, profile:profiles!user_id(id, first_name, last_name, avatar_url, is_admin, is_bam_coach)")
    .eq("org_id", orgId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  // Reverse so oldest first for natural chat reading order
  return (await fillProfiles(data || [])).reverse();
}

export async function postOrgMessage(orgId, body) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const trimmed = (body || "").trim();
  if (!trimmed) return null;
  const { data, error } = await supabase
    .from("org_messages")
    .insert({ org_id: orgId, user_id: user.id, body: trimmed.slice(0, 4000) })
    .select("*, profile:profiles!user_id(id, first_name, last_name, avatar_url, is_admin, is_bam_coach)")
    .single();
  if (error) throw error;
  return data;
}

// ── Org Portal: Quizzes ─────────────────────────────────────────────────

export async function listOrgQuizzes(orgId, { adminView = false } = {}) {
  let q = supabase.from("org_quizzes").select("*").eq("org_id", orgId).order("created_at", { ascending: false });
  if (!adminView) q = q.eq("is_published", true);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createOrgQuiz({ orgId, title, description = "" }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("org_quizzes")
    .insert({ org_id: orgId, created_by: user.id, title, description })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function publishOrgQuiz(quizId, isPublished) {
  const { error } = await supabase
    .from("org_quizzes")
    .update({ is_published: !!isPublished })
    .eq("id", quizId);
  if (error) throw error;
}

export async function deleteOrgQuiz(quizId) {
  const { error } = await supabase.from("org_quizzes").delete().eq("id", quizId);
  if (error) throw error;
}

export async function listOrgQuizQuestions(quizId) {
  const { data, error } = await supabase
    .from("org_quiz_questions")
    .select("*")
    .eq("quiz_id", quizId)
    .order("position", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addOrgQuizQuestion({ quizId, prompt, questionType, options, position }) {
  const { data, error } = await supabase
    .from("org_quiz_questions")
    .insert({
      quiz_id: quizId,
      prompt,
      question_type: questionType,
      options: options || null,
      position: position ?? 0,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteOrgQuizQuestion(questionId) {
  const { error } = await supabase.from("org_quiz_questions").delete().eq("id", questionId);
  if (error) throw error;
}

export async function submitQuizResponse({ quizId, questionId, answer, isCorrect = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("org_quiz_responses")
    .upsert({
      quiz_id: quizId,
      question_id: questionId,
      user_id: user.id,
      answer,
      is_correct: isCorrect,
      submitted_at: new Date().toISOString(),
    }, { onConflict: "question_id,user_id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function listMyQuizResponses(quizId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from("org_quiz_responses")
    .select("*")
    .eq("quiz_id", quizId)
    .eq("user_id", user.id);
  if (error) throw error;
  return data || [];
}

// ── Org Portal: Film submissions ────────────────────────────────────────

export async function listOrgFilmSubmissions(orgId) {
  const { data, error } = await supabase
    .from("org_film_submissions")
    .select("*, profile:profiles!user_id(id, first_name, last_name, avatar_url)")
    .eq("org_id", orgId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return fillProfiles(data || []);
}

export async function createOrgFilmSubmission({ orgId, title, description = "", muxPlaybackId = null, videoUrl = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  // For now we accept either a Mux playback id (preferred) or a raw video
  // URL in the description so coaches can paste a Drive / YouTube link
  // while we build the proper Mux upload flow.
  const desc = videoUrl
    ? `${description}\n\nVideo URL: ${videoUrl}`.trim()
    : description;
  const { data, error } = await supabase
    .from("org_film_submissions")
    .insert({
      org_id: orgId,
      user_id: user.id,
      title,
      description: desc,
      mux_playback_id: muxPlaybackId,
      // A pasted link is ready the moment it's saved (it sat at "uploading").
      status: (muxPlaybackId || videoUrl) ? "ready" : "uploading",
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function listFilmComments(submissionId) {
  const { data, error } = await supabase
    .from("org_film_comments")
    .select("*, profile:profiles!user_id(id, first_name, last_name, avatar_url, is_admin)")
    .eq("submission_id", submissionId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return fillProfiles(data || []);
}

export async function postFilmComment(submissionId, body, timestampSec = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("org_film_comments")
    .insert({
      submission_id: submissionId,
      user_id: user.id,
      body,
      timestamp_sec: timestampSec,
    })
    .select("*, profile:profiles!user_id(id, first_name, last_name, avatar_url, is_admin)")
    .single();
  if (error) throw error;
  return data;
}

// ── Mux direct upload — reuses the existing Vercel API endpoints from
//    practice/VideoUploadModal so we have one upload pattern across the
//    app. Caller passes onProgress(0–100) for the UI progress bar.
//    Returns { muxPlaybackId, muxAssetId, durationSec }.

export async function muxDirectUpload(file, { metadata = {}, onProgress } = {}) {
  // 1. Create direct upload (server signs with Mux secret)
  const createResp = await fetch("/api/create-mux-upload", {
    method: "POST",
    headers: await assistHeaders(),
    body: JSON.stringify({ metadata, purpose: "org_film" }),
  });
  if (!createResp.ok) throw new Error("Failed to create upload session");
  const { id: uploadId, url: uploadUrl } = await createResp.json();

  // 2. PUT file to Mux with progress
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Upload failed")));
    xhr.open("PUT", uploadUrl);
    xhr.send(file);
  });

  // 3. Poll for asset-ready (caller stays on the screen)
  const maxAttempts = 90;          // ~3 minutes at 2s
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const sResp = await fetch(`/api/mux-asset-status?uploadId=${uploadId}`, { headers: await authHeaders() });
    if (!sResp.ok) continue;
    const status = await sResp.json();
    if (status.assetStatus === "ready" && status.playbackId) {
      return {
        muxUploadId: uploadId,
        muxAssetId: status.assetId,
        muxPlaybackId: status.playbackId,
        durationSec: Math.round(status.duration || 0),
      };
    }
    if (status.errors?.length > 0) {
      throw new Error(status.errors[0]?.message || "Mux processing failed");
    }
  }
  throw new Error("Mux processing timed out — check back in a minute.");
}

// ── Org Portal: Scheduled calls ─────────────────────────────────────────

export async function listOrgCalls(orgId, { upcoming = null } = {}) {
  let q = supabase.from("org_calls").select("*").eq("org_id", orgId);
  if (upcoming === true) q = q.gte("scheduled_at", new Date().toISOString()).order("scheduled_at", { ascending: true });
  else if (upcoming === false) q = q.lt("scheduled_at", new Date().toISOString()).order("scheduled_at", { ascending: false });
  else q = q.order("scheduled_at", { ascending: false });
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createOrgCall({ orgId, title, description, scheduledAt, durationMin, meetingUrl }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("org_calls")
    .insert({
      org_id: orgId,
      created_by: user.id,
      title, description: description || null,
      scheduled_at: scheduledAt,
      duration_min: durationMin || 60,
      meeting_url: meetingUrl || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateOrgCall(callId, patch) {
  const { error } = await supabase.from("org_calls").update(patch).eq("id", callId);
  if (error) throw error;
}

export async function deleteOrgCall(callId) {
  const { error } = await supabase.from("org_calls").delete().eq("id", callId);
  if (error) throw error;
}

// ── Cert progress (admin) ───────────────────────────────────────────────
// One row per member with cert_status, via the server (see adminOrg).
export async function adminOrgCertProgress(orgId) {
  const { members } = await adminOrgMembers(orgId);
  return (members || []).map(m => ({
    user_id: m.user_id, role: m.role, joined_at: m.joined_at,
    profile: m, cert_status: m.cert_status || "not_started",
  }));
}

// ── Org resources (documents) ───────────────────────────────────────────
// Private bucket "org-resources", files under "<org_id>/"; rows in
// org_resources. Members read, org owner/admin and BAM admins write
// (supabase/migrations 2026-10-15e). Downloads are signed links.
const ORG_RES_BUCKET = "org-resources";

export async function listOrgResources(orgId) {
  const { data, error } = await supabase.from("org_resources")
    .select("id, org_id, title, description, storage_path, file_name, mime, size_bytes, uploaded_by, created_at")
    .eq("org_id", orgId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function uploadOrgResource(orgId, file, { title, description } = {}) {
  if (!file) throw new Error("Pick a file first.");
  const { data: { user } } = await supabase.auth.getUser();
  const safe = String(file.name || "file").replace(/[^\w.\-]+/g, "_").slice(-120);
  const path = `${orgId}/${Date.now()}-${safe}`;
  const up = await supabase.storage.from(ORG_RES_BUCKET).upload(path, file, {
    contentType: file.type || undefined, upsert: false,
  });
  if (up.error) throw up.error;
  const { data, error } = await supabase.from("org_resources").insert({
    org_id: orgId,
    title: (title || "").trim() || file.name,
    description: (description || "").trim() || null,
    storage_path: path, file_name: file.name, mime: file.type || null, size_bytes: file.size ?? null,
    uploaded_by: user?.id || null,
  }).select().single();
  if (error) {
    // Don't leave an orphaned file behind a failed row.
    await supabase.storage.from(ORG_RES_BUCKET).remove([path]).catch(() => {});
    throw error;
  }
  return data;
}

export async function orgResourceUrl(row, { download = false } = {}) {
  const { data, error } = await supabase.storage.from(ORG_RES_BUCKET)
    .createSignedUrl(row.storage_path, 3600, download ? { download: row.file_name || true } : undefined);
  if (error) throw error;
  return data?.signedUrl || null;
}

export async function deleteOrgResource(row) {
  const { error } = await supabase.from("org_resources").delete().eq("id", row.id);
  if (error) throw error;
  await supabase.storage.from(ORG_RES_BUCKET).remove([row.storage_path]).catch(() => {});
}

// ── Org Portal: certification due dates and action items (2026-10-22) ──
// Tables and RPCs in supabase/migrations/2026-10-22c_org_head_coach_tasks_due_dates.sql.

/** A missing table or function: the migration hasn't been run yet. */
export const needsMigration = (e) => /42P01|42883|PGRST202|PGRST205|does not exist|Could not find the (table|function)/i.test(`${e?.code || ""} ${e?.message || ""}`);

export async function listCertDueDates(orgId) {
  const { data, error } = await supabase.from("org_cert_due_dates").select("*").eq("org_id", orgId).order("due_on");
  if (error) throw error;
  return data || [];
}

/** One due date: a unit, a day, and a member (or the whole org when userId is null). Replaces the one it overlaps. */
export async function setCertDueDate({ orgId, unitId, userId = null, dueOn, note = null }) {
  let q = supabase.from("org_cert_due_dates").delete().eq("org_id", orgId).eq("unit_id", unitId);
  q = userId ? q.eq("user_id", userId) : q.is("user_id", null);
  const { error: delErr } = await q;
  if (delErr) throw delErr;
  const { data, error } = await supabase.from("org_cert_due_dates")
    .insert({ org_id: orgId, unit_id: unitId, user_id: userId, due_on: dueOn, note: note || null }).select("*").single();
  if (error) throw error;
  return data;
}

export async function deleteCertDueDate(id) {
  const { error } = await supabase.from("org_cert_due_dates").delete().eq("id", id);
  if (error) throw error;
}

/** Every member with their certification progress and unit finish times (leads only). */
export async function orgCertRoster(orgId) {
  const { data, error } = await supabase.rpc("org_cert_roster", { p_org_id: orgId });
  if (error) throw error;
  return data || [];
}

/** The signed-in member's own progress and unit finish times. */
export async function myCertProgress() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { progress: {}, completions: {} };
  const [{ data: row }, { data: done }] = await Promise.all([
    supabase.from("user_data").select("value").eq("user_id", user.id).eq("key", "cert_progress").maybeSingle(),
    supabase.from("cert_unit_completions").select("unit_id, completed_at").eq("user_id", user.id),
  ]);
  return { userId: user.id, progress: row?.value || {}, completions: Object.fromEntries((done || []).map(c => [String(c.unit_id), c.completed_at])) };
}

export async function listOrgTasks(orgId) {
  const { data, error } = await supabase.from("org_tasks").select("*").eq("org_id", orgId)
    .order("done_at", { ascending: true, nullsFirst: true }).order("due_on", { ascending: true, nullsFirst: false }).order("created_at");
  if (error) throw error;
  return data || [];
}

/** Several action items at once: [{ assigneeId, title, detail, dueOn, callId }]. */
export async function createOrgTasks(orgId, items) {
  const rows = items.map(t => ({ org_id: orgId, assignee_id: t.assigneeId, title: String(t.title).trim().slice(0, 300),
    detail: t.detail ? String(t.detail).slice(0, 4000) : null, due_on: t.dueOn || null, call_id: t.callId || null }));
  const { data, error } = await supabase.from("org_tasks").insert(rows).select("*");
  if (error) throw error;
  return data || [];
}

export async function updateOrgTask(id, patch) {
  const { data, error } = await supabase.from("org_tasks").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id).select("*").single();
  if (error) throw error;
  return data;
}

export async function deleteOrgTask(id) {
  const { error } = await supabase.from("org_tasks").delete().eq("id", id);
  if (error) throw error;
}

/** Check a task off (or back on): the member, for their own; leads for anyone's. */
export async function setOrgTaskDone(id, done) {
  const { data, error } = await supabase.rpc("set_org_task_done", { p_task_id: id, p_done: !!done });
  if (error) throw error;
  return data;
}
